import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import type { SvgIconProps, SxProps, Theme } from "@mui/material";
import { Stack, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import type { ComponentType, JSX, PropsWithChildren } from "react";
import { useMemo } from "react";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import useMergedSx from "@entrilia/shared/hooks/useMergedSx";
import type {
  ConditionValidationResult,
  DimensionValidationResult,
  MeasuresValidationResult,
} from "../../../../../../../api/types/svTypes.ts";
import FunctionIcon from "@entrilia/shared/icons/FunctionIcon.tsx";
import HashIcon from "@entrilia/shared/icons/HashIcon.tsx";
import NavigationParentIcon from "@entrilia/shared/icons/NavigationParentIcon.tsx";

interface DimensionValidationProps {
  field: DimensionValidationResult;
  sx?: SxProps<Theme>;
}

export function DimensionValidation({ field, sx }: DimensionValidationProps) {
  const showBorder = useMemo(() => {
    return field.customConditions.length > 0 || !field.valid;
  }, [field.customConditions.length, field.valid]);

  const mergedSx = useMergedSx(sx, {
    borderColor: showBorder ? "divider" : "transparent",
  });

  return (
    <ItemValidation field={field} Icon={HashIcon} containerSx={mergedSx} subMessage={<ItemValid field={field} />}>
      {field.customConditions.map((cc) => (
        <ItemValidationComponent
          key={cc.name}
          field={cc}
          Icon={NavigationParentIcon}
          containerSx={{ py: 0.5 }}
          subMessage={<ItemValid field={cc} />}
        />
      ))}
    </ItemValidation>
  );
}

interface ConditionValidationProps {
  field: ConditionValidationResult;
  sx?: SxProps<Theme>;
}
export function ConditionValidation({ field, sx }: ConditionValidationProps) {
  return <ItemValidation field={field} Icon={HashIcon} containerSx={sx} subMessage={<ItemValid field={field} />} />;
}

export function MeasureValidation({ field }: { field: MeasuresValidationResult }) {
  const showBorder = useMemo(() => {
    return field.customConditions.length > 0 || !field.valid;
  }, [field.customConditions.length, field.valid]);

  return (
    <>
      <ItemValidation
        field={field}
        Icon={FunctionIcon}
        containerSx={{ borderColor: showBorder ? "divider" : "transparent" }}
        itemSx={{
          py: 0.5,
          borderBottom: field.customConditions.length > 0 ? "1px dashed" : "none",
          borderBottomColor: "divider",
        }}
        subMessage={<ItemValid field={field} />}
      >
        {field.customConditions.map((cc) => (
          <ItemValidationComponent
            key={cc.name}
            field={cc}
            Icon={NavigationParentIcon}
            containerSx={{ py: 0.5 }}
            subMessage={<ItemValid field={cc} />}
          />
        ))}
      </ItemValidation>
    </>
  );
}

interface ItemValidationProps {
  field: Pick<DimensionValidationResult, "caption" | "isCustomField" | "valid">;
  Icon: ComponentType<SvgIconProps>;
  itemSx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  subMessage?: JSX.Element;
}

function ItemValidation({
  field,
  itemSx,
  containerSx,
  Icon,
  subMessage,
  children,
}: PropsWithChildren<ItemValidationProps>) {
  return (
    <Stack
      sx={{
        border: "1px solid",
        borderRadius: 1,
        px: 0.5,
        bgcolor: field.valid ? "inherit" : "background.grey",
        ...containerSx,
        borderColor: "divider",
      }}
    >
      <ItemValidationComponent field={field} Icon={Icon} containerSx={itemSx} subMessage={subMessage} />
      <Stack>{children}</Stack>
    </Stack>
  );
}

function ItemValid({ field }: { field: Pick<DimensionValidationResult, "valid" | "message"> }) {
  if (!field.valid) {
    return (
      <Typography variant="caption" color="secondary" sx={{ pl: 3.5 }}>
        {field.message}
      </Typography>
    );
  }

  return null;
}

interface ItemValidationComponentProps {
  field: Pick<DimensionValidationResult, "caption" | "isCustomField" | "valid">;
  Icon: ComponentType<SvgIconProps>;
  containerSx?: SxProps<Theme>;
  subMessage?: JSX.Element;
}

function ItemValidationComponent({ field, containerSx, Icon, subMessage }: ItemValidationComponentProps) {
  return (
    <>
      <Stack sx={{ ...containerSx }}>
        <Stack direction="row" useFlexGap spacing={1} sx={{ alignItems: "center" }}>
          <Icon sx={{ color: field.isCustomField ? blue[600] : "secondary.light" }} />
          <Typography
            sx={{
              textDecoration: !field.valid ? "line-through" : "none",
              textDecorationColor: (theme) => theme.palette.secondary.main,
            }}
          >
            {field.caption}
          </Typography>
          <HorizontalFill />
          {field.valid && <CheckRoundedIcon color="success" />}
          {!field.valid && <WarningRoundedIcon color="warning" />}
        </Stack>
        {subMessage}
      </Stack>
    </>
  );
}
